import React from 'react';
import PropTypes from 'prop-types';
import { withTracker } from 'meteor/react-meteor-data';
import { Meteor } from 'meteor/meteor';
import uuid from 'uuid/v1';

import {
  Stack,
  Button,
  Card,
  CardMedia,
  IconButton,
  Tooltip,
  Typography,
  Modal,
  Box,
  Menu,
  MenuItem
} from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseIcon from '@mui/icons-material/Close';

import { isEmpty, filter, isEqual, find, map, cloneDeep } from 'lodash';
import MapIcon from '@mui/icons-material/Map';
import Loading from '../../Loading/index';

import { ELEMENT_TYPES } from '../../../../api/AnnotationTemplates/AnnotationTemplates';
import * as templateOps from '../../../../api/AnnotationTemplates/annotation-template-operations';
import * as mapOps from '../../../../api/Maps/map-operations';

import Maps from '../../../../api/Maps/Maps';
import MapChronologyCollage from '../../MapChronologyCollage/index';
import Layers from '../../../../api/Layers/Layers';
import TemplateImapsItemEdit from './TemplateImapsItemEdit';
import MapContainerViewMap from '../../../containers/MapContainerViewMap/index';
import OpenModalFromUrl from '../../OpenModalFromUrl/index';
import ButtonOnMap from '../../../containers/ButtonOnMap/index';
import TablePagination from '../../TablePagination/index';

import { renderIconButton, renderTable } from '../../MaterialMUIComponent';

const TemplateMapsItem = ({
  deployEnv,
  elementItem,
  updateAnnotationFields,
  maps,
  layers,
  loading,
  isNotesComponent,
  updatedAnnotation,
  selectedAnnotation,
  isAdvancedMode,
  menuBasicMode,
  setMenuBasicMode,
  previewMode,
  ...rest
}) => {
  if (loading) return <Loading />;
  const schemaDefault = {
    nameMap: 'none',
    mapId: '',
    zoom: 6,
    heightMap: '400',
    centerMode: 'none',
    coordsCenter: [],
    coordinates: [],
    linkMap: '',
    imgMap: 'https://sd1.d3.ipsilum.com/files_uploaded/default_multiImap.jpg',
    onlyFullScreen: false
  };

  const multiMapsSchema = templateOps.getElementMultipleMaps(elementItem);
  const isMultiMap = isEqual(templateOps.getElementType(elementItem), ELEMENT_TYPES.MULTIPLEIMAPS);
  const [mapSchema, setMapSchema] = React.useState(templateOps.getElementMap(elementItem));

  const [currentPage, setCurrentPage] = React.useState(1);
  const itemsPerPage = 7;
  const totalCount = multiMapsSchema.length;
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const [multiImapsToShow, setMultiImapsToShow] = React.useState(
    filter(multiMapsSchema, (element, index) => index < itemsPerPage)
  );

  const startMapSelected =
    !isMultiMap && !isEmpty(mapSchema.mapId)
      ? find(maps, item => isEqual(item._id, mapSchema.mapId))
      : {};
  const startLinkSelected = !isMultiMap && !isEmpty(mapSchema.linkMap) ? mapSchema.linkMap : '';
  const startHeight = !isMultiMap ? mapSchema.heightMap : '400';
  const [mapSelected, setMapSelected] = React.useState({
    map: !isEmpty(startMapSelected) ? startMapSelected : {},
    height: startHeight,
    iframe: startLinkSelected
  });
  const [leafletMap, setLeafletMap] = React.useState({});

  const [openChronologyMaps, setOpenChronologyMaps] = React.useState(false);
  const [openFullScreen, setOpenFullScreen] = React.useState(false);
  const openMenuBasicMode = Boolean(menuBasicMode.anchor);

  const indexMultiMap = schema =>
    multiMapsSchema.indexOf(
      find(multiMapsSchema, item => {
        const filterIndex = isEqual(item.mapId, schema.mapId);
        return filterIndex;
      })
    );

  const changePage = pageNumber => {
    const indexFilter = itemsPerPage * pageNumber;
    const prevIndexFilter = itemsPerPage * (pageNumber - 1);
    const newDatasToShow = filter(
      multiMapsSchema,
      (element, index) => index >= prevIndexFilter && index < indexFilter
    );
    setMultiImapsToShow(newDatasToShow);
    setCurrentPage(pageNumber);
  };

  const handleViewMultiMap = multiMap => {
    if (isEqual(multiMap.nameMap, mapSchema.nameMap) && !multiMap.onlyFullScreen) setMapSchema({});
    else setMapSchema(multiMap);
    if (!isEmpty(multiMap.mapId)) {
      const viewMap = find(maps, mapItem => isEqual(mapItem._id, multiMap.mapId));
      setMapSelected({
        map: !isEmpty(viewMap) ? viewMap : {},
        height: multiMap.heightMap,
        iframe: ''
      });
    }
    if (!isEmpty(multiMap.linkMap))
      setMapSelected({ map: {}, height: multiMap.heightMap, iframe: multiMap.linkMap });
    if (multiMap.onlyFullScreen) setOpenFullScreen(true);
  };

  const mapComponentFullScreen = () => {
    if (openFullScreen) {
      return (
        <MapContainerViewMap
          deployEnv={deployEnv}
          baseMaps={mapOps.getLayerObjectsArray(mapSelected.map, layers, false)}
          layers={mapOps.getLayerObjectsArray(mapSelected.map, layers)}
          map={mapSelected.map}
          leafletMapId="imaps-multi-fullScreen"
          height={`${window.innerHeight + 125}px`}
          showReloadPosition={false}
        />
      );
    }
    return '';
  };

  const handleFastMap = nameMap => {
    let newMultiSchema = [];
    if (isEqual(nameMap, 'none')) {
      setMapSelected({ ...mapSelected, map: {} });
      setMapSchema(schemaDefault);
      if (!isMultiMap) updateAnnotationFields(elementItem, 'map', schemaDefault);
    } else {
      const schema = find(multiMapsSchema, item => isEqual(item.nameMap, nameMap));
      const newMap = find(maps, item => isEqual(item.name, nameMap));
      const newMapSchema = !isEmpty(schema)
        ? schema
        : { ...schemaDefault, mapId: newMap._id, nameMap, linkMap: '' };
      setMapSelected({ ...mapSelected, map: newMap });
      setMapSchema(newMapSchema);
      if (isMultiMap) {
        const cloneMultiSchema = cloneDeep(multiMapsSchema);
        if (!isEqual(indexMultiMap(newMapSchema), -1)) {
          cloneMultiSchema.splice(indexMultiMap(newMapSchema), 1, newMapSchema);
          newMultiSchema = cloneDeep(cloneMultiSchema);
        } else newMultiSchema = [...multiMapsSchema, newMapSchema];
        if (!isEmpty(newMultiSchema)) {
          setMultiImapsToShow(filter(newMultiSchema, (element, index) => index < itemsPerPage));
        }
      }
      const valuSchemaToSave = !isMultiMap ? 'map' : 'multipleMaps';
      const mapSchemaToSave = !isMultiMap ? newMapSchema : newMultiSchema;
      updateAnnotationFields(elementItem, valuSchemaToSave, mapSchemaToSave);
    }
  };

  React.useEffect(() => {
    if (!isEmpty(elementItem)) {
      let newElement = {};
      if (!isMultiMap) {
        newElement = templateOps.getElementMap(elementItem);
      } else {
        const elementMultiMap = templateOps.getElementMultipleMaps(elementItem);
        newElement = !isEmpty(elementMultiMap) ? elementMultiMap[0] : {};
        setMultiImapsToShow(filter(elementMultiMap, (element, index) => index < itemsPerPage));
      }
      const newMap = !isEmpty(newElement.mapId)
        ? find(maps, item => isEqual(item._id, newElement.mapId))
        : {};
      const newLink = !isEmpty(newElement.linkMap) ? newElement.linkMap : '';
      setMapSelected({
        map: !isEmpty(newMap) && !isMultiMap ? newMap : {},
        height: newElement.heightMap,
        iframe: newLink
      });
      setMapSchema(newElement);
    }
  }, [elementItem]);

  React.useEffect(() => {
    if (!isEmpty(leafletMap)) {
      if (!isEmpty(mapSelected.map) && !isEmpty(mapSchema)) {
        if (!isEqual(mapSchema.centerMode, 'none') && !isEmpty(mapSchema.coordsCenter))
          leafletMap.setView(mapSchema.coordsCenter);
      }
    }
  }, [leafletMap]);

  const elementsTable = [
    {
      row: 1,
      column: 0,
      rowJustifyContent: templateOps.getElemenPosition(elementItem),
      isShowRow: true,
      showElement: isMultiMap,
      elements: [
        multiImapsToShow.map((item, index) => {
          return (
            <Tooltip
              key={`tooltip-showMulti-${templateOps.getElementOrder(elementItem)}-${index + 1}`}
              title={item.nameMap}
            >
              <Card
                key={`card-${templateOps.getElementOrder(elementItem)}-${index + 1}`}
                style={{ height: '120px', width: '120px', marginRight: '2%' }}
              >
                <Button
                  key={`buttonCard-${templateOps.getElementOrder(elementItem)}`}
                  style={{ padding: 0 }}
                  onClick={() => handleViewMultiMap(item)}
                >
                  <CardMedia
                    key={`cardMedia-${templateOps.getElementOrder(elementItem)}-${index + 1}`}
                    component="img"
                    style={{ height: '100%' }}
                    image={item.imgMap}
                  />
                </Button>
              </Card>
            </Tooltip>
          );
        })
      ]
    },
    {
      row: 2,
      column: 0,
      rowJustifyContent: 'flex-end',
      isShowRow: true,
      showElement: !isEmpty(multiMapsSchema),
      elements: [
        renderIconButton({
          key: `mapChrono-${templateOps.getElementOrder(elementItem)}`,
          onClick: () => setOpenChronologyMaps(true),
          icon: <MapIcon />,
          titleTooltip: 'Maps chronological order',
          style: {
            color: '#2A3F90',
            backgroundColor: 'white',
            height: '34px',
            width: '34px',
            borderColor: '#2A3F90',
            marginRight: '2%'
          }
        }),
        totalPages > 1 && (
          <Typography
            marginTop="1%"
            marginRight="1%"
          >{`${currentPage} - ${totalPages}`}</Typography>
        ),
        <TablePagination
          key={uuid()}
          currentPage={currentPage}
          isPaginationOn
          itemsPerPage={itemsPerPage}
          stretchView
          totalCount={totalCount}
          changePage={changePage}
        />
      ]
    }
  ];

  return (
    <>
      <Box key={`menuBasic-${templateOps.getElementOrder(elementItem)}`} id="box-menuBasic">
        <Menu
          key={`menu-basic-${templateOps.getElementOrder(elementItem)}`}
          open={openMenuBasicMode}
          anchorEl={menuBasicMode.anchor}
          onClose={() => setMenuBasicMode({ ...menuBasicMode, anchor: null })}
          style={{
            left: `${menuBasicMode.left}px`,
            top: `${menuBasicMode.top}px`
          }}
        >
          {map([{ name: 'none' }].concat(maps), element => {
            return (
              <MenuItem
                autoFocus={isEqual(mapSelected.map._id, element._id)}
                selected={isEqual(mapSelected.map._id, element._id)}
                key={`${templateOps.getElementOrder(elementItem)}-${element._id}`}
                onClick={() => {
                  handleFastMap(element.name);
                  setMenuBasicMode({ ...menuBasicMode, anchor: null });
                }}
              >{`${element.name}`}</MenuItem>
            );
          })}
        </Menu>
      </Box>
      {!isAdvancedMode && (
        <>
          {isMultiMap && (
            <>
              {renderTable(elementsTable)}
              <Modal
                key={`modal-imaps-${templateOps.getElementOrder(elementItem)}`}
                open={openChronologyMaps}
                onClose={() => setOpenChronologyMaps(false)}
              >
                <Box
                  key={`box-modal-imaps-${templateOps.getElementOrder(elementItem)}`}
                  style={{
                    zIndex: 1800,
                    position: 'absolute',
                    backgroundColor: 'white',
                    height: '95%',
                    width: '95%',
                    top: '2.5vh',
                    left: '2.5vw'
                  }}
                >
                  <Stack
                    key={`row-modal-editMap-${templateOps.getElementOrder(elementItem)}`}
                    direction="row"
                    display="flex"
                    justifyContent="flex-start"
                    marginTop="1%"
                  >
                    {renderIconButton({
                      key: `open-chronoMaps-${templateOps.getElementOrder(elementItem)}`,
                      onClick: () => setOpenChronologyMaps(false),
                      icon: <CloseIcon />,
                      titleTooltip: '',
                      style: {
                        color: '#2A3F90',
                        backgroundColor: 'white',
                        height: '34px',
                        width: '34px',
                        borderColor: '#2A3F90'
                      }
                    })}
                  </Stack>

                  <MapChronologyCollage
                    maps={maps}
                    listChronologyMaps={filter(
                      multiMapsSchema,
                      multiMap => !isEmpty(multiMap.mapId)
                    )}
                    deployEnv={deployEnv}
                    layers={layers}
                  />
                </Box>
              </Modal>
            </>
          )}
          {!isEmpty(mapSelected.map) && !isEmpty(mapSchema) && (
            <>
              {!mapSchema.onlyFullScreen && (
                <Stack
                  key={`mapBox-editMap-${templateOps.getElementOrder(elementItem)}`}
                  id="mapBox"
                  style={{
                    position: 'relative',
                    width: '100%',
                    height: `${Number(mapSchema.heightMap)}px`,
                    display: 'flex',
                    justifyContent: templateOps.getElemenPosition(elementItem)
                  }}
                >
                  <MapContainerViewMap
                    deployEnv={deployEnv}
                    baseMaps={mapOps.getLayerObjectsArray(mapSelected.map, layers, false)}
                    layers={mapOps.getLayerObjectsArray(mapSelected.map, layers)}
                    leafletMapId={`imaps-${mapOps.getId(
                      mapSelected.map
                    )}-${templateOps.getElementType(elementItem)}`}
                    handleMapLoaded={(isLoaded, mapL) => {
                      if (isLoaded) setLeafletMap(mapL);
                    }}
                    map={mapSelected.map}
                    height="100vh"
                    showReloadPosition={false}
                  />
                  {templateOps.getEnabledFullScreen(elementItem) && (
                    <ButtonOnMap numberOfButtonsRight={1}>
                      <IconButton
                        key={`iconbutton-onMap-${templateOps.getElementOrder(elementItem)}`}
                        style={{
                          color: '#2A3F90',
                          backgroundColor: 'white',
                          height: '34px',
                          width: '34px',
                          borderColor: '#2A3F90'
                        }}
                        disabled={false}
                        onClick={() => setOpenFullScreen(true)}
                      >
                        <FullscreenIcon />
                      </IconButton>
                    </ButtonOnMap>
                  )}
                </Stack>
              )}
              <OpenModalFromUrl
                isFullScreen
                isImage={false}
                isLoading
                openModal={openFullScreen}
                setOpenModal={setOpenFullScreen}
                url=""
                title={mapSchema.nameMap}
                isComponent
                component={mapComponentFullScreen}
                idComponent="imaps-fullScreen"
              />
            </>
          )}

          {!isEmpty(mapSelected.iframe) && !isEmpty(mapSchema) && !mapSchema.onlyFullScreen && (
            <Stack
              key={`${templateOps.getElementOrder(elementItem)}-${mapSelected.iframe}-stack`}
              display="flex"
              justifyContent={templateOps.getElemenPosition(elementItem)}
            >
              <iframe
                key={`${templateOps.getElementOrder(elementItem)}-${mapSelected.iframe}-showIframe`}
                src={mapSelected.iframe}
                width="100%"
                height="400px"
                align="center"
                title="mapShared"
              />
            </Stack>
          )}
        </>
      )}
      {isAdvancedMode && (
        <>
          <TemplateImapsItemEdit
            elementItem={elementItem}
            updateAnnotationFields={updateAnnotationFields}
            maps={maps}
            layers={layers}
            updatedAnnotation={updatedAnnotation}
            {...rest}
          />
        </>
      )}
    </>
  );
};
TemplateMapsItem.defaultProps = {
  deployEnv: 'gmv',
  maps: [],
  layers: [],
  updatedAnnotation: {},
  selectedAnnotation: {},
  isNotesComponent: false
};

TemplateMapsItem.propTypes = {
  deployEnv: PropTypes.string,
  elementItem: PropTypes.object.isRequired,
  updateAnnotationFields: PropTypes.func.isRequired,
  maps: PropTypes.array,
  layers: PropTypes.array,
  loading: PropTypes.bool.isRequired,
  updatedAnnotation: PropTypes.object,
  selectedAnnotation: PropTypes.object,
  isNotesComponent: PropTypes.bool,
  isAdvancedMode: PropTypes.bool.isRequired,
  menuBasicMode: PropTypes.object.isRequired,
  setMenuBasicMode: PropTypes.func.isRequired,
  previewMode: PropTypes.string.isRequired
};

export default withTracker(() => {
  const mapsSub = Meteor.isClient && Meteor.subscribe('maps');
  const layersSub = Meteor.subscribe('layers');
  return {
    loading: !mapsSub.ready() || !layersSub.ready(),
    maps: Maps.find({}, { sort: { name: 1 } }).fetch(),
    layers: Layers.find({ deleted: { $eq: 'no' } }).fetch()
  };
})(TemplateMapsItem);
